// Events API for React Native admin app
// Based on admin-web events implementation

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import { 
  IEvent, 
  IEventListResponse, 
  IEventResidentResponse,
  IEventFilterModel, 
  IEventSortModel,
  IEventPhoto,
} from '@/types/admin/events';
import { IPaginationModel } from '@/types/admin/dataTable';
import { MAX_ATTACHMENTS } from '@/constants/residents';

// Build query string for events list (matching admin-web pattern)
const buildEventsQuery = (params: {
  sortModel: IEventSortModel[];
  filterModel: IEventFilterModel;
  paginationModel: IPaginationModel;
  selectedPropertyId: number;
  searchQuery?: string;
}): URLSearchParams => {
  const { sortModel, filterModel, paginationModel, selectedPropertyId, searchQuery } = params;
  const searchParams = new URLSearchParams();

  // Property ID (required)
  searchParams.append('propertyId', selectedPropertyId.toString());

  // Pagination
  searchParams.append('pageNum', paginationModel.page.toString());
  searchParams.append('pageSize', paginationModel.pageSize.toString());

  // Search query
  if (searchQuery && searchQuery.trim()) {
    searchParams.append('searchQuery', searchQuery.trim());
  }

  // Sorting
  if (sortModel && sortModel.length > 0) {
    const sort = sortModel[0];
    searchParams.append('sortBy', sort.field);
    searchParams.append('sortOrder', sort.sort);
  }

  // Filters
  if (filterModel.status && filterModel.status.length > 0) {
    filterModel.status.forEach(status => {
      searchParams.append('status', status);
    });
  }

  if (filterModel.dateRange) {
    if (filterModel.dateRange.start) {
      searchParams.append('startDate', filterModel.dateRange.start);
    }
    if (filterModel.dateRange.end) {
      searchParams.append('endDate', filterModel.dateRange.end);
    }
  }

  if (filterModel.month && filterModel.year) {
    searchParams.append('month', filterModel.month.toString());
    searchParams.append('year', filterModel.year.toString());
  }

  return searchParams;
};

// Build query string for event residents (matching admin-web pattern)
const buildEventResidentsQuery = (params: {
  propertyId: number;
  eventId: number;
  pageNum: number;
  hasRsvp?: boolean;
  notRsvp?: boolean;
  checkedIn?: boolean;
  notCheckedIn?: boolean;
  searchQuery?: string;
}): URLSearchParams => {
  const { propertyId, eventId, pageNum, hasRsvp, notRsvp, checkedIn, notCheckedIn, searchQuery } = params;
  const searchParams = new URLSearchParams();

  searchParams.append('propertyId', propertyId.toString());
  searchParams.append('eventId', eventId.toString());
  searchParams.append('pageNum', pageNum.toString());

  if (hasRsvp === true) {
    searchParams.append('hasRsvp', 'true');
  }
  if (notRsvp === true) {
    searchParams.append('hasRsvp', 'false');
  }
  if (checkedIn === true) {
    searchParams.append('checkedIn', 'true');
  }
  if (notCheckedIn === true) {
    searchParams.append('checkedIn', 'false');
  }
  if (searchQuery && searchQuery.trim()) {
    searchParams.append('searchQuery', searchQuery.trim());
  }

  return searchParams;
};

// Convert form values to FormData (matching admin-web pattern)
export const convertToFormData = (
  formValues: Record<string, unknown>,
  propertyId: number | undefined,
  eventId?: number, // Optional event ID for edit mode
): FormData => {
  const {
    name,
    description,
    location,
    rsvpLimit,
    startTime,
    endTime,
    photos,
    internalNotes,
    publishTime,
    publishType,
  } = formValues as any;

  // Create a simple Draft.js-like structure for the description
  // Since we don't have Draft.js in React Native, we'll create a minimal structure
  const rawDescriptionContent = {
    blocks: [
      {
        key: 'block1',
        text: description || '',
        type: 'unstyled',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {},
      },
    ],
    entityMap: {},
  };

  // Double-encode the JSON to match web app behavior
  const descriptionJson = JSON.stringify(rawDescriptionContent);
  const doubleEncodedDescriptionJson = JSON.stringify(descriptionJson);

  const formData = new FormData();
  formData.append('propertyId', propertyId?.toString() ?? '');
  formData.append('name', name);
  formData.append('description', description); // Plain text description
  formData.append('descriptionJson', doubleEncodedDescriptionJson); // Required by backend
  formData.append('descriptionHtml', `<p>${description || ''}</p>`); // Simple HTML version
  formData.append('location', location);
  formData.append('address', location);
  formData.append('rsvpLimit', rsvpLimit.toString());
  
  if (internalNotes) {
    formData.append('internalNotes', internalNotes);
  }
  
  // Handle publishTime based on publishType
  if (publishType === 'publishNow') {
    // For "Publish Now", always set publishTime to current time
    formData.append('publishTime', new Date().toISOString());
  } else if (publishType === 'scheduled' && publishTime) {
    // For scheduled, use the provided publishTime
    formData.append('publishTime', publishTime);
  }
  // For 'draft', don't append publishTime (leave it null/undefined)

  if (startTime) {
    formData.append('startTime', startTime);
  }

  if (endTime) {
    formData.append('endTime', endTime);
  }

  // Handle photos - send both existing photo IDs to preserve AND new photo uploads
  // This ensures deleted photos are actually removed from the backend
  if (photos && Array.isArray(photos)) {
    // Collect existing photo IDs (photos with photoUrl but no new file)
    const existingPhotoIds: number[] = [];
    
    (photos as IEventPhoto[]).forEach((photo: any) => {
      const hasNative = !!photo?.uri && (photo.uri.startsWith('file://') || photo.uri.startsWith('content://') || photo.uri.startsWith('ph://'));
      const hasWeb = !!photo?.file && (typeof (photo.file as any).name === 'string' || (typeof Blob !== 'undefined' && photo.file instanceof Blob));
      
      // If it's a new file upload, append to photos
      if (hasNative || hasWeb) {
        const uri: string | undefined = photo?.uri;
        const isLocalNative = !!uri && (uri.startsWith('file://') || uri.startsWith('content://') || uri.startsWith('ph://'));
        const isWebFile = !!photo?.file && (typeof (photo.file as any).name === 'string' || (typeof Blob !== 'undefined' && photo.file instanceof Blob));

        if (isLocalNative) {
          const filePart = {
            uri: uri!,
            type: photo.type || 'image/jpeg',
            name: photo.name || 'photo.jpg',
          } as any;
          formData.append('photos', filePart);
        } else if (isWebFile) {
          const webFile: any = photo.file as Blob;
          formData.append('photos', webFile);
        }
      } else if (photo.id && photo.photoUrl) {
        // Existing photo - collect ID to preserve
        existingPhotoIds.push(photo.id);
      }
    });
    
    // Send existing photo IDs to preserve (backend will delete others)
    // IMPORTANT: Always send this when editing (even if empty array) so backend knows to delete removed photos
    if (eventId !== undefined) {
      formData.append('existingPhotoIds', JSON.stringify(existingPhotoIds));
    }
  }

  return formData;
};

// RTK Query API slice for events
export const eventsApi = createApi({
  reducerPath: 'eventsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Events', 'EventDetails', 'EventResidents'],
  endpoints: (builder) => ({
    // Get events list with filtering, sorting, and pagination
    getEvents: builder.query<IEventListResponse, {
      sortModel: IEventSortModel[];
      filterModel: IEventFilterModel;
      paginationModel: IPaginationModel;
      selectedPropertyId: number;
      searchQuery?: string;
    }>({
      query: (params) => {
        const searchParams = buildEventsQuery(params);
        return {
          url: `/api/v1/events/list?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: IEvent[]): IEventListResponse => ({
        data: response || [],
        count: response?.length || 0,
      }),
      providesTags: (result, error, arg) => [
        { type: 'Events', id: `property-${arg.selectedPropertyId}` },
        { type: 'Events', id: 'LIST' },
      ],
    }),

    // Lazy query version for manual triggering (matching admin-web pattern)
    getEventsLazy: builder.query<IEvent[], string>({
      query: (queryParam) => ({
        url: `/api/v1/events/list${queryParam}`,
        method: 'GET',
      }),
      providesTags: ['Events'],
    }),

    // Get event residents with filtering
    getEventResidents: builder.query<IEventResidentResponse, {
      propertyId: number;
      eventId: number;
      pageNum?: number;
      hasRsvp?: boolean;
      notRsvp?: boolean;
      checkedIn?: boolean;
      notCheckedIn?: boolean;
      searchQuery?: string;
    }>({
      query: (params) => {
        const searchParams = buildEventResidentsQuery({
          ...params,
          pageNum: params.pageNum || 0,
        });
        return {
          url: `/api/v1/events/residents?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { eventId }) => [
        { type: 'EventResidents', id: `event-${eventId}` },
      ],
    }),

    // Create event
    createEvent: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: '/api/v1/events',
        method: 'POST',
        body: formData,
        formData: true,
      }),
      invalidatesTags: ['Events'],
    }),

    // Update event
    updateEvent: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: '/api/v1/events',
        method: 'PATCH',
        body: formData,
        formData: true,
      }),
      invalidatesTags: ['Events', 'EventDetails'],
    }),

    // Delete event
    deleteEvent: builder.mutation<any, IEvent>({
      query: (event) => ({
        url: '/api/v1/events',
        method: 'DELETE',
        body: event,
      }),
      invalidatesTags: ['Events'],
    }),

    // Update resident RSVP/check-in status
    updateResidentRsvp: builder.mutation<any, {
      eventId: number;
      residentId: number;
      hasRsvp?: boolean;
      checkedIn?: boolean;
    }>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.append('eventId', params.eventId.toString());
        searchParams.append('residentId', params.residentId.toString());
        if (params.checkedIn !== undefined) searchParams.append('checkedIn', params.checkedIn.toString());
        if (params.hasRsvp !== undefined) searchParams.append('hasRsvp', params.hasRsvp.toString());
        return {
          url: `/api/v1/events/resident-checkin?${searchParams.toString()}`,
          method: 'PATCH',
        };
      },
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'EventResidents', id: `event-${eventId}` },
      ],
    }),
  }),
});

export const {
  useGetEventsQuery,
  useLazyGetEventsQuery,
  useLazyGetEventsLazyQuery,
  useGetEventResidentsQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useUpdateResidentRsvpMutation,
} = eventsApi;
